import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, ArrowLeft, Send, FileDown, DollarSign, Copy, Ban, Trash2, Settings, Search, X, Receipt, Link2, CheckCircle2,
  MoreHorizontal, Wallet, Save, Timer, Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useProject } from '@/lib/ProjectContext';
import { useModules, hasModule } from '@/lib/modules';
import { getCurrentUser } from '@/lib/auth';
import {
  invoicesAPI, fmtMoney, fmtDay, todayISO, addDaysISO, openBlob, computeTotals, INVOICE_STATUS,
} from '@/lib/financeAPI';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const sel = 'w-full h-10 rounded-md border border-input px-2 text-sm bg-white';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const isAdmin = () => ['owner', 'admin'].includes(getCurrentUser()?.role) || getCurrentUser()?.is_super_admin;

const StatusPill = ({ status }) => {
  const s = INVOICE_STATUS[status] || INVOICE_STATUS.draft;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

const Tile = ({ label, value, note, tone }) => (
  <div className={`${card} p-4`}>
    <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold">{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone === 'red' ? 'text-[#A62B1F]' : 'text-[#1E2A33]'}`}>{value}</div>
    {note && <div className="text-xs text-[#5C6670] mt-0.5">{note}</div>}
  </div>
);

// ── List ─────────────────────────────────────────────────────────────────────
const FILTERS = [
  { key: '', label: 'All' }, { key: 'draft', label: 'Drafts' }, { key: 'unpaid', label: 'Unpaid' },
  { key: 'overdue', label: 'Overdue' }, { key: 'paid', label: 'Paid' },
];

const InvoiceList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    invoicesAPI.list({ status: filter || undefined, q: q || undefined }).then((r) => setData(r.data)).catch((e) => err(e, 'Could not load invoices'));
  }, [filter, q]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const create = async () => {
    setCreating(true);
    try { const r = await invoicesAPI.create({}); navigate(`/invoices/${r.data.id}`); } catch (e) { err(e, 'Could not start an invoice'); setCreating(false); }
  };

  const s = data?.summary || {};
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-semibold text-[#1E2A33] mr-auto" style={serif}>Invoices</h1>
        {isAdmin() && <Button variant="outline" onClick={() => navigate('/invoices/settings')}><Settings className="w-4 h-4 mr-1" />Settings</Button>}
        <Button className={red} onClick={create} disabled={creating}><Plus className="w-4 h-4 mr-1" />New invoice</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Outstanding" value={fmtMoney(s.outstanding)} note="Sent and not yet paid" />
        <Tile label="Overdue" value={fmtMoney(s.overdue)} note={`${s.overdue_count || 0} invoice${s.overdue_count === 1 ? '' : 's'}`} tone={s.overdue_count ? 'red' : null} />
        <Tile label="Received this year" value={fmtMoney(s.paid_this_year)} />
        <Tile label="Drafts" value={s.draft_count || 0} note="Not sent yet" />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="flex items-center gap-2 flex-wrap p-3 border-b border-[#E3DDD1]">
          <div className="flex gap-1 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === f.key ? 'bg-[#1E2A33] text-white' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{f.label}</button>
            ))}
          </div>
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-2.5 top-3 text-[#94A3B8]" />
            <Input className="pl-8" placeholder="Number or client" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        {!data ? <div className="p-6 text-sm text-[#5C6670]">Loading…</div> : data.invoices.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt className="w-8 h-8 mx-auto text-[#C9C0B0] mb-2" />
            <p className="text-sm text-[#5C6670]">{filter || q ? 'No invoices match.' : 'No invoices yet. Start one and it will be numbered for you.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FBF8F2] text-[11px] uppercase tracking-wider text-[#5C6670]">
                <tr><th className="text-left p-3">Number</th><th className="text-left p-3">Client</th><th className="text-left p-3 hidden md:table-cell">Project</th>
                  <th className="text-left p-3">Issued</th><th className="text-left p-3 hidden sm:table-cell">Due</th><th className="text-right p-3">Total</th>
                  <th className="text-right p-3 hidden sm:table-cell">Balance</th><th className="p-3">Status</th></tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-[#F0EBE1] hover:bg-[#FBF8F2] cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <td className="p-3 font-semibold text-[#1E2A33] whitespace-nowrap">{inv.number}</td>
                    <td className="p-3"><div className="text-[#1E2A33] font-medium">{inv.client?.company || inv.client?.name || <span className="text-[#94A3B8] font-normal">No client yet</span>}</div>
                      {inv.client?.company && inv.client?.name && <div className="text-xs text-[#5C6670]">{inv.client.name}</div>}</td>
                    <td className="p-3 text-[#5C6670] hidden md:table-cell">{inv.project_name || ''}</td>
                    <td className="p-3 whitespace-nowrap text-[#5C6670]">{fmtDay(inv.issue_date)}</td>
                    <td className="p-3 whitespace-nowrap text-[#5C6670] hidden sm:table-cell">{fmtDay(inv.due_date)}</td>
                    <td className="p-3 text-right whitespace-nowrap">{fmtMoney(inv.totals?.total, inv.currency)}</td>
                    <td className="p-3 text-right whitespace-nowrap hidden sm:table-cell font-medium">{inv.display_status === 'void' ? '' : fmtMoney(inv.totals?.balance, inv.currency)}</td>
                    <td className="p-3 text-center"><StatusPill status={inv.display_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Client picker ────────────────────────────────────────────────────────────
export const ClientPicker = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => invoicesAPI.clients(q).then((r) => setHits(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q, open]);
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-3 text-[#94A3B8]" />
        <Input className="pl-8" placeholder="Find a contact…" value={q} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
        {open && hits.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-[#E3DDD1] bg-white shadow-lg max-h-64 overflow-y-auto">
            {hits.map((h) => (
              <button key={h.contact_id} type="button" className="w-full text-left px-3 py-2 hover:bg-[#FBF8F2]"
                onMouseDown={() => { onChange({ ...value, ...h, address: h.address || value.address || '' }); setQ(''); setOpen(false); }}>
                <div className="text-sm text-[#1E2A33]">{h.company || h.name}</div>
                <div className="text-xs text-[#5C6670]">{[h.company ? h.name : null, h.email].filter(Boolean).join(' · ')}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div><span className="text-[11px] text-[#5C6670]">Company (billed)</span><Input value={value.company || ''} onChange={(e) => set({ company: e.target.value })} placeholder="Leave blank to bill a person" /></div>
        <div><span className="text-[11px] text-[#5C6670]">Contact person</span><Input value={value.name || ''} onChange={(e) => set({ name: e.target.value })} /></div>
        <div><span className="text-[11px] text-[#5C6670]">Email</span><Input type="email" value={value.email || ''} onChange={(e) => set({ email: e.target.value })} /></div>
        <div><span className="text-[11px] text-[#5C6670]">Also send to (cc)</span><Input value={value.cc || ''} onChange={(e) => set({ cc: e.target.value })} placeholder="accounts@client.com" /></div>
      </div>
      <div><span className="text-[11px] text-[#5C6670]">Billing address</span><Textarea rows={2} value={value.address || ''} onChange={(e) => set({ address: e.target.value })} /></div>
      {value.contact_id && <div className="text-[11px] text-[#5C6670]">Linked to a contact · <button type="button" className="underline" onClick={() => set({ contact_id: null })}>unlink</button></div>}
    </div>
  );
};

// ── Dialogs ──────────────────────────────────────────────────────────────────
const SendDialog = ({ open, onOpenChange, invoice, onSent }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(null);
  useEffect(() => {
    if (!open) return;
    setF(null); setBackup(null);
    invoicesAPI.emailDraft(invoice.id).then((r) => setF({ ...r.data, package: true })).catch((e) => err(e, 'Could not prepare the email'));
    invoicesAPI.attachments(invoice.id).then((r) => setBackup(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const backupCount = backup ? backup.attachments.length + (invoice.include_receipts !== false ? backup.receipts.length : 0) + (invoice.include_timesheet && backup.hours ? 1 : 0) : 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Email invoice {invoice.number}</DialogTitle>
          <DialogDescription>The PDF goes as an attachment with a link to view it online. Replies come to {f?.reply_to || 'you'}.</DialogDescription></DialogHeader>
        {!f ? <div className="text-sm text-[#5C6670]">Preparing…</div> : (
          <div className="space-y-3">
            <div><Label>To</Label><Input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></div>
            <div><Label>Cc (optional)</Label><Input value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} /></div>
            <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></div>
            <div><Label>Message</Label><Textarea rows={7} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></div>
            {backupCount > 0 && (
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={f.package} onChange={(e) => setF({ ...f, package: e.target.checked })} />
                <span>Include the backup in one PDF <span className="block text-xs text-[#5C6670]">{backupCount} item{backupCount === 1 ? '' : 's'} after the invoice{backup.attachments.some((a) => !a.in_pdf) ? '; files that can\'t go in a PDF are attached separately' : ''}</span></span></label>
            )}
            <Button className={`w-full ${red}`} disabled={busy} onClick={async () => {
              setBusy(true);
              try { const r = await invoicesAPI.send(invoice.id, f); toast.success(`Sent to ${f.to}`); onOpenChange(false); onSent(r.data); } catch (e) { err(e, 'Could not send'); } finally { setBusy(false); }
            }}><Send className="w-4 h-4 mr-1" />{busy ? 'Sending…' : 'Send invoice'}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog = ({ open, onOpenChange, invoice, methods, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ amount: invoice.totals?.balance || '', date: todayISO(), method: methods?.[0] || 'e-Transfer', reference: '' }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record a payment</DialogTitle><DialogDescription>Balance due {fmtMoney(invoice.totals?.balance, invoice.currency)}. Part payments are fine.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
            <div><Label>Date received</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
          <div><Label>How</Label><select className={sel} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{(methods || []).map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><Label>Reference (optional)</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="e-Transfer ref, cheque no." /></div>
          <Button className={`w-full ${red}`} onClick={async () => {
            try { const r = await invoicesAPI.addPayment(invoice.id, { ...f, amount: Number(f.amount) }); toast.success(r.data.status === 'paid' ? 'Paid in full' : 'Payment recorded'); onOpenChange(false); onSaved(r.data); } catch (e) { err(e, 'Could not record it'); }
          }}>Record payment</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UnbilledDialog = ({ open, onOpenChange, projectId, contactId, already, onAdd }) => {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [scope, setScope] = useState('project');
  useEffect(() => {
    if (!open) return;
    setRows(null); setPicked({});
    const params = scope === 'project' && projectId ? { project_id: projectId } : scope === 'client' && contactId ? { contact_id: contactId } : {};
    invoicesAPI.unbilled(params).then((r) => setRows(r.data.filter((x) => !already.includes(x.id)))).catch((e) => err(e, 'Could not load expenses'));
  }, [open, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = (x) => x.kind === 'mileage' ? `Mileage: ${x.mileage?.from} to ${x.mileage?.to} (${x.mileage?.distance} km)`
    : x.kind === 'per_diem' ? `Meal per diem${x.per_diem?.location ? `, ${x.per_diem.location}` : ''}` : (x.vendor || x.category || 'Expense');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Bill expenses</DialogTitle><DialogDescription>Billable expenses not yet on an invoice. Receipts are billed before tax; the invoice adds tax on top.</DialogDescription></DialogHeader>
        <div className="flex gap-1 mb-2">
          {[['project', 'This project'], ['client', 'This client'], ['all', 'All']].map(([k, l]) => (
            <button key={k} disabled={(k === 'project' && !projectId) || (k === 'client' && !contactId)} onClick={() => setScope(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-40 ${scope === k ? 'bg-[#1E2A33] text-white' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{l}</button>
          ))}
        </div>
        {!rows ? <div className="text-sm text-[#5C6670]">Loading…</div> : rows.length === 0 ? <div className="text-sm text-[#5C6670]">Nothing waiting to be billed here. Mark expenses as billable when you add them.</div> : (
          <div className="max-h-80 overflow-y-auto divide-y divide-[#F0EBE1] border border-[#E3DDD1] rounded-lg">
            {rows.map((x) => (
              <label key={x.id} className="flex items-start gap-2 p-2 text-sm cursor-pointer hover:bg-[#FBF8F2]">
                <input type="checkbox" className="mt-1" checked={!!picked[x.id]} onChange={(e) => setPicked({ ...picked, [x.id]: e.target.checked ? x : undefined })} />
                <span className="flex-1"><span className="text-[#1E2A33]">{label(x)}</span>
                  <span className="block text-xs text-[#5C6670]">{fmtDay(x.date)} · {[x.project_name, x.reason].filter(Boolean).join(' · ')}</span></span>
                <span className="whitespace-nowrap">{fmtMoney(x.amount - (x.tax_amount || 0))}</span>
              </label>
            ))}
          </div>
        )}
        <Button className={`w-full ${red}`} disabled={!Object.values(picked).some(Boolean)} onClick={() => {
          onAdd(Object.values(picked).filter(Boolean).map((x) => ({ description: `${label(x)}${x.reason ? ` · ${x.reason}` : ''} (${fmtDay(x.date)})`, quantity: 1, unit_price: Math.round((x.amount - (x.tax_amount || 0)) * 100) / 100, expense_id: x.id })));
          onOpenChange(false);
        }}>Add to invoice</Button>
      </DialogContent>
    </Dialog>
  );
};

// Hours logged in Time, gathered into invoice lines
const TIME_GROUPS = [
  ['activity', 'By activity', 'Prep & research, 10 h'],
  ['description', 'By note', 'One line per different note'],
  ['day', 'By day', 'Jul 7: 6.5 h'],
  ['entry', 'Every entry', 'Each log on its own line'],
  ['one', 'One line', 'Professional services, 24 h'],
];

const dateSpan = (rows) => {
  const ds = rows.map((r) => r.date).sort();
  const a = fmtDay(ds[0], { month: 'short', day: 'numeric' }); const b = fmtDay(ds[ds.length - 1], { month: 'short', day: 'numeric' });
  return a === b ? a : `${a} to ${b}`;
};

const groupTime = (rows, mode) => {
  const groups = new Map();
  rows.forEach((r) => {
    const label = mode === 'activity' ? (r.activity || 'Professional services')
      : mode === 'description' ? (r.description || r.activity || 'Professional services')
        : mode === 'day' ? fmtDay(r.date, { weekday: 'short', month: 'short', day: 'numeric' })
          : mode === 'entry' ? `${[r.activity, r.description].filter(Boolean).join(': ') || 'Professional services'} (${fmtDay(r.date, { month: 'short', day: 'numeric' })})`
            : 'Professional services';
    const key = mode === 'entry' ? r.id : `${label}|${r.rate}`;   // different rates stay on separate lines
    if (!groups.has(key)) groups.set(key, { label, rate: r.rate, rows: [] });
    groups.get(key).rows.push(r);
  });
  return [...groups.values()].map((g) => {
    const hours = Math.round(g.rows.reduce((a, r) => a + r.hours, 0) * 100) / 100;
    const notes = mode === 'activity' ? [...new Set(g.rows.map((r) => r.description).filter(Boolean))].slice(0, 4).join('; ') : '';
    const desc = mode === 'entry' || mode === 'day' ? g.label : `${g.label} (${dateSpan(g.rows)})${notes ? `\n${notes}` : ''}`;
    return { description: desc, quantity: hours, unit_price: g.rate, time_entry_ids: g.rows.map((r) => r.id) };
  });
};

const BillTimeDialog = ({ open, onOpenChange, projectId, contactId, already, onAdd, apply = false }) => {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [scope, setScope] = useState('project');
  const [mode, setMode] = useState('activity');
  useEffect(() => {
    if (!open) return;
    setRows(null);
    const sc = scope === 'project' && !projectId ? (contactId ? 'client' : 'all') : scope === 'client' && !contactId ? 'all' : scope;
    if (sc !== scope) { setScope(sc); return; }
    const params = sc === 'project' ? { project_id: projectId } : sc === 'client' ? { contact_id: contactId } : {};
    invoicesAPI.unbilledTime(params).then((r) => {
      const list = r.data.filter((x) => !already.includes(x.id));
      setRows(list); setPicked(Object.fromEntries(list.map((x) => [x.id, true])));
    }).catch((e) => err(e, 'Could not load time'));
  }, [open, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = (rows || []).filter((x) => picked[x.id]);
  const lines = groupTime(chosen, mode);
  const total = chosen.reduce((a, x) => a + x.amount, 0);
  const hours = chosen.reduce((a, x) => a + x.hours, 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{apply ? 'Apply hours to this invoice' : 'Bill time'}</DialogTitle>
          <DialogDescription>{apply ? "For a retainer billed up front: the hours are recorded as covered by this invoice. What it charges doesn't change." : 'Billable hours not yet on an invoice, at the rate each was logged at. You can still edit the lines after adding them.'}</DialogDescription></DialogHeader>
        <div className="flex gap-1 flex-wrap">
          {[['project', 'This project'], ['client', 'This client'], ['all', 'All']].map(([k, l]) => (
            <button key={k} disabled={(k === 'project' && !projectId) || (k === 'client' && !contactId)} onClick={() => setScope(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-40 ${scope === k ? 'bg-[#1E2A33] text-white' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{l}</button>
          ))}
        </div>
        {!rows ? <div className="text-sm text-[#5C6670]">Loading…</div> : rows.length === 0 ? <div className="text-sm text-[#5C6670] py-4">No unbilled hours here. Log time under Time, or with the timer at the top of the page.</div> : (
          <>
            <div className="max-h-60 overflow-y-auto divide-y divide-[#F0EBE1] border border-[#E3DDD1] rounded-lg">
              <label className="flex items-center gap-2 p-2 text-xs font-semibold text-[#5C6670] bg-[#FBF8F2] sticky top-0">
                <input type="checkbox" checked={chosen.length === rows.length} onChange={(e) => setPicked(Object.fromEntries(rows.map((x) => [x.id, e.target.checked])))} />Select all ({rows.length})
              </label>
              {rows.map((x) => (
                <label key={x.id} className="flex items-start gap-2 p-2 text-sm cursor-pointer hover:bg-[#FBF8F2]">
                  <input type="checkbox" className="mt-1" checked={!!picked[x.id]} onChange={(e) => setPicked({ ...picked, [x.id]: e.target.checked })} />
                  <span className="flex-1 min-w-0"><span className="text-[#1E2A33]">{[x.activity, x.description].filter(Boolean).join(' · ') || 'No note'}</span>
                    <span className="block text-xs text-[#5C6670]">{fmtDay(x.date)} · {[x.project_name, x.contact_name].filter(Boolean).join(' · ')}</span></span>
                  <span className="whitespace-nowrap text-right">{x.hours} h<span className="block text-xs text-[#5C6670]">{fmtMoney(x.rate)}/h</span></span>
                </label>
              ))}
            </div>
            {!apply && <div>
              <div className="text-xs font-semibold text-[#5C6670] mb-1">How it appears on the invoice</div>
              <div className="flex gap-1 flex-wrap">{TIME_GROUPS.map(([k, l, hint]) => (
                <button key={k} title={hint} onClick={() => setMode(k)} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${mode === k ? 'bg-[#1E2A33] text-white' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{l}</button>
              ))}</div>
              <div className="mt-2 rounded-lg border border-[#E3DDD1] divide-y divide-[#F0EBE1] text-sm max-h-48 overflow-y-auto">
                {lines.map((l, i) => (
                  <div key={i} className="flex gap-2 p-2"><span className="flex-1 whitespace-pre-line text-[#1E2A33]">{l.description}</span>
                    <span className="whitespace-nowrap text-[#5C6670]">{l.quantity} h × {fmtMoney(l.unit_price)}</span><span className="w-24 text-right">{fmtMoney(l.quantity * l.unit_price)}</span></div>
                ))}
              </div>
            </div>}
          </>
        )}
        <Button className={`w-full ${red}`} disabled={!chosen.length} onClick={() => { onAdd(apply ? chosen.map((x) => x.id) : lines); onOpenChange(false); }}>
          {apply ? `Apply ${Math.round(hours * 100) / 100} h to this invoice` : `Add ${Math.round(hours * 100) / 100} h (${fmtMoney(total)}) to invoice`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Backup sent with the invoice: everything here goes out with it as one PDF
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const BackupCard = ({ inv, form, set, mods }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => invoicesAPI.attachments(inv.id).then((r) => setData(r.data)).catch(() => {}), [inv.id]);
  useEffect(() => { load(); }, [load, inv.updated_at]);
  const locked = inv.status === 'void';
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try { const r = await invoicesAPI.uploadAttachments(inv.id, files); setData(r.data); toast.success(`${files.length} file${files.length === 1 ? '' : 's'} attached`); }
    catch (e) { err(e, 'Could not attach'); } finally { setBusy(false); }
  };
  const move = async (i, d) => {
    const ids = data.attachments.map((a) => a.id); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const r = await invoicesAPI.orderAttachments(inv.id, ids); setData(r.data);
  };
  const atts = data?.attachments || [];
  const separate = atts.filter((a) => !a.in_pdf);
  return (
    <div className={`${card} p-4 space-y-2 ${drag ? 'ring-2 ring-[#A62B1F]' : ''}`}
      onDragOver={(e) => { if (!locked) { e.preventDefault(); setDrag(true); } }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!locked) upload(e.dataTransfer.files); }}>
      <div className="flex items-center"><div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold flex-1">Backup &amp; attachments</div>
        {!locked && <button className="text-xs font-semibold text-[#A62B1F]" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Attaching…' : '+ Attach files'}</button>}</div>
      <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
      <p className="text-[11px] text-[#94A3B8]">Sent with the invoice as one PDF: the invoice first, then everything below. Photos become pages. Drop files here.</p>
      {atts.map((a, i) => (
        <div key={a.id} className="flex items-center gap-1.5 text-xs py-0.5">
          <Paperclip className="w-3 h-3 text-[#94A3B8] shrink-0" />
          <button className="flex-1 truncate text-left hover:underline" title="Open" onClick={async () => { const r = await invoicesAPI.attachment(inv.id, a.id); openBlob(r.data); }}>{a.filename}</button>
          <span className="text-[#94A3B8] shrink-0">{fmtSize(a.size)}</span>
          {!a.in_pdf && <span className="text-[10px] px-1 rounded bg-[#F3EFE7] text-[#5C6670] shrink-0" title="Can't be merged into a PDF, so it's attached to the email on its own">separate</span>}
          {!locked && <>
            <button className="text-[#94A3B8] hover:text-[#1E2A33] disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">↑</button>
            <button className="text-[#94A3B8] hover:text-[#1E2A33] disabled:opacity-30" disabled={i === atts.length - 1} onClick={() => move(i, 1)} title="Move down">↓</button>
            <button className="text-[#94A3B8] hover:text-[#A62B1F]" title="Remove" onClick={async () => { const r = await invoicesAPI.removeAttachment(inv.id, a.id); setData(r.data); }}><X className="w-3 h-3" /></button>
          </>}
        </div>
      ))}
      {hasModule(mods, 'expenses') && data?.receipts?.length > 0 && (
        <label className="flex items-center gap-2 text-xs text-[#5C6670]"><input type="checkbox" checked={form.include_receipts} disabled={locked} onChange={(e) => set({ include_receipts: e.target.checked })} />
          Receipts from billed expenses ({data.receipts.length})</label>
      )}
      {hasModule(mods, 'time') && data?.hours > 0 && (
        <label className="flex items-center gap-2 text-xs text-[#5C6670]"><input type="checkbox" checked={form.include_timesheet} disabled={locked} onChange={(e) => set({ include_timesheet: e.target.checked })} />
          Timesheet for the {data.hours} h on this invoice</label>
      )}
      {(atts.length > 0 || (form.include_receipts && data?.receipts?.length) || (form.include_timesheet && data?.hours)) ? (
        <button className="text-xs font-semibold text-[#A62B1F]" onClick={async () => { try { const r = await invoicesAPI.packagePdf(inv.id); openBlob(r.data); } catch (e) { err(e, 'Could not build the package'); } }}>
          Preview what the client gets{separate.length ? ` (+ ${separate.length} separate file${separate.length === 1 ? '' : 's'})` : ''}
        </button>
      ) : null}
    </div>
  );
};

// Hours worked under a retainer invoice (applied after it's sent; the invoice amount doesn't change)
const AppliedHours = ({ inv, form, set, projectId, contactId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => invoicesAPI.appliedTime(inv.id).then((r) => setData(r.data)).catch(() => {}), [inv.id]);
  useEffect(() => { load(); }, [load]);
  const locked = inv.status === 'void';
  const cap = Number(form.retainer_hours || 0);
  const used = data?.hours || 0;
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return (
    <div className={`${card} p-4 space-y-2`}>
      <div className="flex items-center"><div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold flex-1">Hours under this invoice</div>
        {!locked && <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => setOpen(true)}>+ Apply hours</button>}</div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[#5C6670]">Retainer</span>
        <Input type="number" step="0.5" min="0" className="w-20 h-8 text-right" value={form.retainer_hours} disabled={locked} onChange={(e) => set({ retainer_hours: e.target.value })} placeholder="0" />
        <span className="text-[#5C6670]">h</span>
      </div>
      {cap > 0 && (
        <div>
          <div className="h-2 rounded-full bg-[#F3EFE7] overflow-hidden"><div className={`h-full ${used > cap ? 'bg-amber-500' : 'bg-[#A62B1F]'}`} style={{ width: `${pct}%` }} /></div>
          <div className="text-xs text-[#5C6670] mt-1">{used} of {cap} h used · {used > cap ? <span className="text-amber-700 font-semibold">{Math.round((used - cap) * 100) / 100} h over</span> : `${Math.round((cap - used) * 100) / 100} h left`}</div>
        </div>
      )}
      {!cap && used > 0 && <div className="text-xs text-[#5C6670]">{used} h applied</div>}
      {(data?.entries || []).map((e) => (
        <div key={e.id} className="flex items-center gap-2 text-xs py-0.5">
          <span className="w-12 text-[#5C6670]">{fmtDay(e.date, { month: 'short', day: 'numeric' })}</span>
          <span className="flex-1 truncate">{[e.activity, e.description].filter(Boolean).join(' · ')}</span>
          <span className="tabular-nums">{e.hours} h</span>
          {!locked && <button className="text-[#94A3B8] hover:text-[#A62B1F]" title="Take off this invoice" onClick={async () => { const r = await invoicesAPI.unapplyTime(inv.id, [e.id]); setData(r.data); }}><X className="w-3 h-3" /></button>}
        </div>
      ))}
      {data?.entries?.length > 0 && (
        <label className="flex items-center gap-2 text-xs text-[#5C6670] pt-1"><input type="checkbox" checked={form.show_time_on_pdf} disabled={locked} onChange={(e) => set({ show_time_on_pdf: e.target.checked })} />List these hours on the PDF and client page</label>
      )}
      {!data?.entries?.length && <p className="text-[11px] text-[#94A3B8]">Billed a retainer up front? Apply the hours you work to it here, even after it's sent or paid.</p>}
      <BillTimeDialog apply open={open} onOpenChange={setOpen} projectId={projectId} contactId={contactId} already={[]}
        onAdd={async (ids) => { try { const r = await invoicesAPI.applyTime(inv.id, ids); setData(r.data); toast.success(`${r.data.hours} h now under ${inv.number}`); } catch (e) { err(e, 'Could not apply the hours'); } }} />
    </div>
  );
};

// ── Editor ───────────────────────────────────────────────────────────────────
const blankItem = (taxId) => ({ description: '', quantity: 1, unit_price: 0, tax_id: taxId || null });

const InvoiceEditor = ({ id }) => {
  const navigate = useNavigate();
  const { projects = [] } = useProject() || {};
  const mods = useModules();
  const [inv, setInv] = useState(null);
  const [form, setForm] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const saved = useRef(null);

  const defaultTax = useRef(null);
  const take = (data) => {
    setInv(data);
    const f = { number: data.number, client: data.client || {}, project_id: data.project_id || '', issue_date: data.issue_date, due_date: data.due_date,
      po_number: data.po_number || '', currency: data.currency,
      items: data.items?.length ? data.items : (data.status === 'draft' ? [blankItem(defaultTax.current)] : []),
      discount: data.discount || { type: 'amount', value: 0 },
      notes: data.notes || '', payment_instructions: data.payment_instructions || '',
      retainer_hours: data.retainer_hours ?? '', show_time_on_pdf: data.show_time_on_pdf !== false,
      include_receipts: data.include_receipts !== false, include_timesheet: !!data.include_timesheet };
    setForm(f); saved.current = JSON.stringify(f); setDirty(false);
  };
  useEffect(() => {
    invoicesAPI.settings().then((r) => { setSettings(r.data); defaultTax.current = r.data.default_tax_id; }).catch(() => {})
      .then(() => invoicesAPI.get(id)).then((r) => take(r.data)).catch(() => { toast.error('Invoice not found'); navigate('/invoices'); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => { const n = { ...f, ...patch }; setDirty(JSON.stringify(n) !== saved.current); return n; });
  const setItem = (i, patch) => set({ items: form.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });

  const taxes = useMemo(() => (inv?.status === 'draft' ? settings?.taxes : inv?.taxes_snapshot) || settings?.taxes || [], [inv, settings]);
  const totals = useMemo(() => (form ? computeTotals(form.items, taxes, form.discount, inv?.payments) : null), [form, taxes, inv]);

  const save = async (quiet) => {
    setSaving(true);
    try {
      const r = await invoicesAPI.update(id, { ...form, project_id: form.project_id || '', items: form.items.map((it) => ({ ...it, quantity: Number(it.quantity || 0), unit_price: Number(it.unit_price || 0) })),
        discount: { ...form.discount, value: Number(form.discount.value || 0) }, retainer_hours: Number(form.retainer_hours || 0) });
      take(r.data); if (!quiet) toast.success('Saved');
      return r.data;
    } catch (e) { err(e, 'Could not save'); return null; } finally { setSaving(false); }
  };
  const saveThen = async (fn) => { if (dirty) { const ok = await save(true); if (!ok) return; } fn(); };

  if (!inv || !form) return <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div>;
  const locked = inv.status === 'void';
  const cur = form.currency;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`${card} p-4 flex items-center gap-2 flex-wrap`}>
        <button className="p-1 -ml-1" onClick={() => (dirty && !window.confirm('Leave without saving your changes?') ? null : navigate('/invoices'))} title="All invoices"><ArrowLeft className="w-4 h-4" /></button>
        <div className="mr-auto min-w-0">
          <div className="flex items-center gap-2"><h2 className="text-xl font-semibold text-[#1E2A33]" style={serif}>Invoice {inv.number}</h2><StatusPill status={inv.display_status} /></div>
          <div className="text-xs text-[#5C6670] mt-0.5">
            {inv.quote_id && <><button className="underline" onClick={() => navigate(`/quotes/${inv.quote_id}`)}>From quote {inv.quote_number}</button>{inv.quote_part ? ` (${String(inv.quote_part).split(':')[0]})` : ''} · </>}
            {inv.sent_at ? `Sent ${fmtDay(String(inv.sent_at).slice(0, 10))}${inv.sent_to ? ` to ${inv.sent_to}` : ''}` : 'Not sent yet'}
            {inv.viewed_at && ` · Opened ${inv.view_count > 1 ? `${inv.view_count} times` : 'by the client'}`}
          </div>
        </div>
        {dirty && <Button variant="outline" onClick={() => save()} disabled={saving}><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>}
        <Button variant="outline" title="The invoice with its backup, as the client gets it" onClick={() => saveThen(async () => { try { const r = await invoicesAPI.packagePdf(id); openBlob(r.data); } catch (e) { err(e, 'Could not make the PDF'); } })}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
        {!locked && inv.status !== 'paid' && <Button variant="outline" onClick={() => saveThen(() => setPayOpen(true))}><DollarSign className="w-4 h-4 mr-1" />Payment</Button>}
        {!locked && <Button className={red} onClick={() => saveThen(() => setSendOpen(true))}><Send className="w-4 h-4 mr-1" />{inv.status === 'draft' ? 'Send' : 'Resend'}</Button>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {inv.status === 'draft' && <DropdownMenuItem onClick={() => saveThen(async () => { const r = await invoicesAPI.markSent(id); take(r.data); toast.success('Marked as sent'); })}><CheckCircle2 className="w-4 h-4 mr-2" />Mark as sent (delivered another way)</DropdownMenuItem>}
            {inv.status !== 'draft' && <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(inv.public_url); toast.success('Client link copied'); }}><Link2 className="w-4 h-4 mr-2" />Copy client link</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => saveThen(async () => { const r = await invoicesAPI.pdf(id); openBlob(r.data); })}><FileDown className="w-4 h-4 mr-2" />Invoice only PDF (no backup)</DropdownMenuItem>
            <DropdownMenuItem onClick={async () => { const r = await invoicesAPI.duplicate(id); toast.success(`Copied to ${r.data.number}`); navigate(`/invoices/${r.data.id}`); }}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>
            <DropdownMenuSeparator />
            {inv.status === 'draft' ? (
              <DropdownMenuItem className="text-[#A62B1F]" onClick={async () => { if (!window.confirm(`Delete draft ${inv.number}?`)) return; await invoicesAPI.remove(id); toast.success('Deleted'); navigate('/invoices'); }}><Trash2 className="w-4 h-4 mr-2" />Delete draft</DropdownMenuItem>
            ) : !locked && (
              <DropdownMenuItem className="text-[#A62B1F]" onClick={async () => { if (!window.confirm(`Void ${inv.number}? It stays in your records marked VOID, and any billed expenses are freed up.`)) return; const r = await invoicesAPI.void(id); take(r.data); toast.success('Voided'); }}><Ban className="w-4 h-4 mr-2" />Void</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <fieldset disabled={locked} className="space-y-4 min-w-0">
          <div className={`${card} p-4 grid md:grid-cols-2 gap-4`}>
            <div><Label className="mb-1 block">Bill to</Label><ClientPicker value={form.client} onChange={(client) => set({ client })} /></div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-[11px] text-[#5C6670]">Invoice number</span><Input value={form.number} onChange={(e) => set({ number: e.target.value })} /></div>
                <div><span className="text-[11px] text-[#5C6670]">PO / reference</span><Input value={form.po_number} onChange={(e) => set({ po_number: e.target.value })} /></div>
                <div><span className="text-[11px] text-[#5C6670]">Issued</span><Input type="date" value={form.issue_date} onChange={(e) => set({ issue_date: e.target.value })} /></div>
                <div><span className="text-[11px] text-[#5C6670]">Due</span><Input type="date" value={form.due_date} onChange={(e) => set({ due_date: e.target.value })} /></div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {[0, 15, 30, 45, 60].map((d) => (
                  <button key={d} type="button" className="text-[11px] px-2 py-1 rounded-full bg-[#F3EFE7] text-[#5C6670] hover:bg-[#E9E2D4]" onClick={() => set({ due_date: addDaysISO(form.issue_date, d) })}>{d === 0 ? 'On receipt' : `Net ${d}`}</button>
                ))}
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-2">
                <div><span className="text-[11px] text-[#5C6670]">Project</span>
                  <select className={sel} value={form.project_id} onChange={(e) => set({ project_id: e.target.value })}>
                    <option value="">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </select></div>
                <div><span className="text-[11px] text-[#5C6670]">Currency</span>
                  <select className={sel} value={form.currency} onChange={(e) => set({ currency: e.target.value })}>{['CAD', 'USD', 'EUR', 'GBP', 'AUD'].map((c) => <option key={c}>{c}</option>)}</select></div>
              </div>
            </div>
          </div>

          {/* Lines */}
          <div className={`${card} p-4`}>
            <div className="hidden sm:grid grid-cols-[1fr_70px_100px_96px_96px_28px] gap-2 text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold pb-1 border-b border-[#E3DDD1]">
              <span>Description</span><span className="text-right">Qty</span><span className="text-right">Rate</span><span>Tax</span><span className="text-right">Amount</span><span />
            </div>
            {form.items.map((it, i) => (
              <div key={it.id || i} className="grid grid-cols-2 sm:grid-cols-[1fr_70px_100px_96px_96px_28px] gap-2 py-2 border-b border-[#F0EBE1] items-start">
                <Textarea rows={1} className="col-span-2 sm:col-span-1 min-h-[40px]" placeholder="What you did or supplied" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} />
                <Input type="number" step="any" className="text-right" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} aria-label="Quantity" />
                <Input type="number" step="0.01" className="text-right" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} aria-label="Rate" />
                <select className={sel} value={it.tax_id || ''} onChange={(e) => setItem(i, { tax_id: e.target.value || null })}>
                  <option value="">No tax</option>{taxes.map((t) => <option key={t.id} value={t.id}>{t.name} {t.rate}%</option>)}
                </select>
                <div className="h-10 flex items-center justify-end text-sm font-medium">{fmtMoney(totals.lines[i], cur)}</div>
                <button type="button" className="h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#A62B1F]" title="Remove line" onClick={() => set({ items: form.items.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
                {it.expense_id && <div className="col-span-2 sm:col-span-6 text-[11px] text-[#5C6670] -mt-1 flex items-center gap-1"><Wallet className="w-3 h-3" />From a billable expense</div>}
                {it.time_entry_ids?.length > 0 && <div className="col-span-2 sm:col-span-6 text-[11px] text-[#5C6670] -mt-1 flex items-center gap-1"><Timer className="w-3 h-3" />From {it.time_entry_ids.length} time entr{it.time_entry_ids.length === 1 ? 'y' : 'ies'}</div>}
              </div>
            ))}
            <div className="flex gap-3 mt-2 flex-wrap">
              <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => set({ items: [...form.items, blankItem(settings?.default_tax_id)] })}>+ Add a line</button>
              {hasModule(mods, 'time') && <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setTimeOpen(true)}>+ Bill time</button>}
              {hasModule(mods, 'expenses') && <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setBillOpen(true)}>+ Bill expenses</button>}
            </div>
            <div className="flex items-center gap-2 mt-4 text-sm">
              <span className="text-[#5C6670]">Discount</span>
              <Input type="number" step="0.01" className="w-24 text-right" value={form.discount.value} onChange={(e) => set({ discount: { ...form.discount, value: e.target.value } })} />
              <select className="h-10 rounded-md border border-input px-2 text-sm bg-white" value={form.discount.type} onChange={(e) => set({ discount: { ...form.discount, type: e.target.value } })}>
                <option value="amount">$</option><option value="percent">%</option>
              </select>
            </div>
          </div>

          <div className={`${card} p-4 grid md:grid-cols-2 gap-4`}>
            <div><Label>Notes to the client</Label><Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
            <div><Label>How to pay</Label><Textarea rows={3} value={form.payment_instructions} onChange={(e) => set({ payment_instructions: e.target.value })} placeholder="e-Transfer to…, bank details, cheques payable to…" /></div>
          </div>
        </fieldset>

        {/* Summary */}
        <div className="space-y-4 lg:sticky lg:top-4">
          <div className={`${card} p-4 text-sm space-y-1.5`}>
            <div className="flex justify-between"><span className="text-[#5C6670]">Subtotal</span><span>{fmtMoney(totals.subtotal, cur)}</span></div>
            {totals.discount > 0 && <div className="flex justify-between"><span className="text-[#5C6670]">Discount</span><span>−{fmtMoney(totals.discount, cur)}</span></div>}
            {totals.taxes.map((t) => <div key={t.id} className="flex justify-between"><span className="text-[#5C6670]">{t.name} ({t.rate}%)</span><span>{fmtMoney(t.amount, cur)}</span></div>)}
            <div className="flex justify-between border-t border-[#1E2A33] pt-2 mt-2 text-base font-semibold"><span>Total</span><span>{fmtMoney(totals.total, cur)}</span></div>
            {totals.paid > 0 && <div className="flex justify-between text-emerald-700"><span>Paid</span><span>−{fmtMoney(totals.paid, cur)}</span></div>}
            <div className="flex justify-between text-lg font-semibold text-[#1E2A33] bg-[#F3EFE7] rounded-lg px-3 py-2 mt-2"><span>Balance due</span><span>{fmtMoney(totals.balance, cur)}</span></div>
            {dirty && <p className="text-[11px] text-amber-700 pt-1">Unsaved changes</p>}
          </div>

          {inv.payments?.length > 0 && (
            <div className={`${card} p-4`}>
              <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold mb-2">Payments</div>
              {inv.payments.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm py-1">
                  <span className="flex-1">{fmtDay(p.date)} · {p.method}{p.reference ? ` · ${p.reference}` : ''}</span>
                  <span className="font-medium">{fmtMoney(p.amount, cur)}</span>
                  <button className="text-[#94A3B8] hover:text-[#A62B1F]" title="Remove payment" onClick={async () => { if (!window.confirm('Remove this payment?')) return; const r = await invoicesAPI.removePayment(id, p.id); take(r.data); }}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          <BackupCard inv={inv} form={form} set={set} mods={mods} />

          {hasModule(mods, 'time') && <AppliedHours inv={inv} form={form} set={set} projectId={form.project_id} contactId={form.client?.contact_id} />}

          <div className={`${card} p-4`}>
            <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold mb-2">Activity</div>
            {[...(inv.history || [])].reverse().slice(0, 12).map((h, i) => (
              <div key={i} className="text-xs text-[#5C6670] py-0.5"><span className="text-[#1E2A33] capitalize">{h.event}</span>
                {h.to ? ` to ${h.to}` : ''}{h.amount ? ` · ${fmtMoney(h.amount, cur)}` : ''}{h.hours ? ` · ${h.hours} h` : ''} · {new Date(h.at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</div>
            ))}
          </div>
        </div>
      </div>

      <SendDialog open={sendOpen} onOpenChange={setSendOpen} invoice={inv} onSent={take} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} invoice={inv} methods={settings?.payment_methods} onSaved={take} />
      <BillTimeDialog open={timeOpen} onOpenChange={setTimeOpen} projectId={form.project_id} contactId={form.client?.contact_id}
        already={form.items.flatMap((x) => x.time_entry_ids || [])}
        onAdd={(lines) => set({ items: [...form.items.filter((x) => x.description || Number(x.unit_price)), ...lines.map((l) => ({ ...l, tax_id: settings?.default_tax_id || null }))] })} />
      <UnbilledDialog open={billOpen} onOpenChange={setBillOpen} projectId={form.project_id} contactId={form.client?.contact_id}
        already={form.items.map((x) => x.expense_id).filter(Boolean)}
        onAdd={(lines) => set({ items: [...form.items.filter((x) => x.description || Number(x.unit_price)), ...lines.map((l) => ({ ...l, tax_id: settings?.default_tax_id || null }))] })} />
    </div>
  );
};

// ── Settings ─────────────────────────────────────────────────────────────────
const InvoiceSettings = () => {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);
  useEffect(() => { invoicesAPI.settings().then((r) => setS(r.data)).catch((e) => err(e, 'Could not load settings')); }, []);
  if (!s) return <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div>;
  const set = (patch) => setS({ ...s, ...patch });
  const field = (k, label, props = {}) => (
    <div><span className="text-[11px] text-[#5C6670]">{label}</span><Input value={s[k] ?? ''} onChange={(e) => set({ [k]: e.target.value })} {...props} /></div>
  );
  const area = (k, label, rows = 3, ph = '') => (
    <div><span className="text-[11px] text-[#5C6670]">{label}</span><Textarea rows={rows} value={s[k] ?? ''} placeholder={ph} onChange={(e) => set({ [k]: e.target.value })} /></div>
  );
  const preview = `${s.prefix || ''}${String(s.next_number || 1).padStart(Number(s.number_padding || 0), '0')}`;
  const save = async () => {
    setSaving(true);
    try {
      const r = await invoicesAPI.updateSettings({ ...s, next_number: Number(s.next_number), number_padding: Number(s.number_padding), terms_days: Number(s.terms_days),
        taxes: s.taxes.map((t) => ({ ...t, rate: Number(t.rate) })) });
      setS(r.data); toast.success('Saved');
    } catch (e) { err(e, 'Could not save'); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-2">
        <button className="p-1 -ml-1" onClick={() => navigate('/invoices')}><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-2xl font-semibold text-[#1E2A33] mr-auto" style={serif}>Invoice settings</h1>
        <Button className={red} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
      </div>
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-semibold text-[#1E2A33]">Your business, as it appears on invoices</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {field('business_name', 'Business name')}
          {field('email', 'Email')}
          {area('address', 'Address', 3)}
          {area('tax_numbers', 'Tax numbers (one per line)', 3, 'HST/GST 12345 6789 RT0001')}
          {field('phone', 'Phone')}
          {field('website', 'Website')}
          <div><span className="text-[11px] text-[#5C6670]">Logo</span>
            <div className="flex items-center gap-2">
              {s.logo_url ? <img src={s.logo_url} alt="" className="h-10 max-w-[140px] object-contain rounded border border-[#E3DDD1] p-1" /> : <span className="text-xs text-[#94A3B8]">No logo yet</span>}
              <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()}>Upload</Button>
              {s.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => set({ logo_url: '' })}>Remove</Button>}
              <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; e.target.value = ''; if (!file) return;
                try { const r = await invoicesAPI.uploadLogo(file); setS({ ...s, logo_url: r.data.logo_url }); toast.success('Logo updated'); } catch (er) { err(er, 'Could not upload'); }
              }} />
            </div></div>
          <div><span className="text-[11px] text-[#5C6670]">Accent colour</span>
            <div className="flex gap-2"><input type="color" className="h-10 w-12 rounded border border-input" value={s.brand_color || '#A62B1F'} onChange={(e) => set({ brand_color: e.target.value })} />
              <Input value={s.brand_color || ''} onChange={(e) => set({ brand_color: e.target.value })} /></div></div>
        </div>
      </div>
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-semibold text-[#1E2A33]">Numbering and terms</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {field('prefix', 'Prefix')}
          {field('next_number', 'Next number', { type: 'number', min: 1 })}
          {field('number_padding', 'Digits', { type: 'number', min: 0, max: 8 })}
          {field('terms_days', 'Due after (days)', { type: 'number', min: 0 })}
          <div><span className="text-[11px] text-[#5C6670]">Currency</span><select className={sel} value={s.currency} onChange={(e) => set({ currency: e.target.value })}>{['CAD', 'USD', 'EUR', 'GBP', 'AUD'].map((c) => <option key={c}>{c}</option>)}</select></div>
        </div>
        <p className="text-xs text-[#5C6670]">Next invoice will be <strong>{preview}</strong>.</p>
      </div>
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-semibold text-[#1E2A33]">Sales taxes</h3>
        <p className="text-xs text-[#5C6670]">Each line on an invoice picks one. Changing a rate here doesn't change invoices already sent.</p>
        {s.taxes.map((t, i) => (
          <div key={t.id || i} className="flex gap-2 items-center">
            <Input className="w-40" value={t.name} onChange={(e) => set({ taxes: s.taxes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
            <Input className="w-24 text-right" type="number" step="0.001" value={t.rate} onChange={(e) => set({ taxes: s.taxes.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)) })} /><span className="text-sm">%</span>
            <label className="text-xs text-[#5C6670] flex items-center gap-1 ml-2"><input type="radio" checked={s.default_tax_id === t.id} onChange={() => set({ default_tax_id: t.id })} disabled={!t.id} />Default</label>
            <button className="text-[#94A3B8] hover:text-[#A62B1F] ml-auto" onClick={() => set({ taxes: s.taxes.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => set({ taxes: [...s.taxes, { name: '', rate: 0 }] })}>+ Add a tax</button>
      </div>
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-semibold text-[#1E2A33]">Payment and email</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {area('payment_instructions', 'How to pay (printed on every invoice)', 4, 'Interac e-Transfer to pay@yourbusiness.com\nCheques payable to…')}
          {area('notes', 'Default note to the client', 4)}
          {field('reply_to', 'Replies go to', { placeholder: 'Leave blank to use the sender\'s email' })}
          {field('footer', 'Footer line (optional)')}
          {field('email_subject', 'Email subject')}
          <div />
          <div className="md:col-span-2">{area('email_message', 'Email message', 5)}</div>
        </div>
        <p className="text-xs text-[#5C6670]">You can use {'{client}'}, {'{number}'}, {'{total}'}, {'{due_date}'} and {'{business}'} in the subject and message.</p>
      </div>
    </div>
  );
};

export default function Invoices() {
  const { id } = useParams();
  return (
    <Layout title="Invoices">
      {id === 'settings' ? <InvoiceSettings /> : id ? <InvoiceEditor key={id} id={id} /> : <InvoiceList />}
    </Layout>
  );
}
